import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { T, Icon, useFonts, PlanBadge } from "./businessTheme";
import {
  MOCK_BUSINESS_ACCOUNT,
  MOCK_PERSONAL_ACCOUNT,
  MOCK_NOTIFICATIONS,
  MOCK_MESSAGES,
  PLAN_LIMITS,
} from "./businessMockData";
import { useBusinessAccount } from "./useBusinessAccount";
import SettingsDrawer from "./SettingsDrawer";
import AppLogo from "../components/AppLogo";

import OverviewSection from "./sections/OverviewSection";
import TeamSection from "./sections/TeamSection";
import BookingsSection from "./sections/BookingsSection";
import LocationsSection from "./sections/LocationsSection";
import BillingSection from "./sections/BillingSection";
import ReportsSection from "./sections/ReportsSection";
import MembersSection from "./sections/MembersSection";

// ─── Nav items (full set — filtered per role below) ───────────────────────────
const NAV_ITEMS = [
  { id: "overview",  label: "Overview",           icon: "LayoutGrid" },
  { id: "team",      label: "My Team",            icon: "Users" },
  { id: "bookings",  label: "Bookings",           icon: "Calendar" },
  { id: "locations", label: "Locations",          icon: "MapPin" },
  { id: "billing",   label: "Invoices & Billing", icon: "FileText" },
  { id: "reports",   label: "Reports",            icon: "BarChart" },
  { id: "members",   label: "Team Members",       icon: "UserCog" },
];

// Which tabs each role can see
const ROLE_TABS = {
  Owner:             ["overview", "team", "bookings", "locations", "billing", "reports", "members"],
  Admin:             ["overview", "team", "bookings", "locations", "reports", "members"],
  "Booking Manager": ["overview", "bookings", "locations"],
  Viewer:            ["overview", "reports"],
};

// ─── Message bubble icon ──────────────────────────────────────────────────────
function MessageIcon({ size = 16, color = "currentColor", strokeWidth = 1.8 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

// ─── Account switcher dropdown ────────────────────────────────────────────────
function AccountSwitcher({ current, onSwitch, hasPersonalAccount, hasBusinessAccount }) {
  const [open, setOpen] = useState(false);
  const canSwitch = hasPersonalAccount && hasBusinessAccount;
  if (!canSwitch) return null;

  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: "flex", alignItems: "center", gap: 9, padding: "7px 12px 7px 7px",
        borderRadius: 11, border: `1px solid ${T.n150}`, background: T.white, cursor: "pointer",
      }}>
        <div style={{
          width: 30, height: 30, borderRadius: 9, background: T.g50, border: `1px solid ${T.g100}`,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11.5, fontWeight: 700, color: T.g700,
        }}>
          {current.type === "business" ? (current.logoInitials ?? current.name?.slice(0,2).toUpperCase()) : current.initials}
        </div>
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: T.n900, lineHeight: 1.2, maxWidth: 150, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{current.name}</div>
          <div style={{ fontSize: 10.5, color: T.n400, textTransform: "capitalize" }}>{current.type} account</div>
        </div>
        <Icon name="ChevronDown" size={14} color={T.n400} strokeWidth={2} />
      </button>

      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div style={{
            position: "absolute", top: "calc(100% + 8px)", left: 0, width: 260,
            background: T.white, border: `1px solid ${T.n150}`, borderRadius: 14,
            boxShadow: "0 12px 36px rgba(0,0,0,.14)", zIndex: 50, overflow: "hidden",
          }}>
            <div style={{ padding: "10px 14px", fontSize: 10.5, fontWeight: 700, color: T.n400, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Switch account
            </div>

            {/* Business option */}
            <button onClick={() => { onSwitch("business"); setOpen(false); }} style={{
              width: "100%", display: "flex", alignItems: "center", gap: 11, padding: "10px 14px",
              background: current.type === "business" ? T.g50 : "transparent", border: "none", cursor: "pointer", fontFamily: T.sans, textAlign: "left",
            }}>
              <div style={{ width: 30, height: 30, borderRadius: 9, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11.5, fontWeight: 700, color: T.g700, flexShrink: 0 }}>
                {current.type === "business" ? (current.logoInitials ?? current.name?.slice(0,2).toUpperCase()) : "B"}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.n900 }}>{current.name}</div>
                <div style={{ fontSize: 11, color: T.n400 }}>Business account</div>
              </div>
              {current.type === "business" && <Icon name="CheckCircle" size={15} color={T.g600} strokeWidth={2} />}
            </button>

            {/* Personal option */}
            <button onClick={() => { onSwitch("personal"); setOpen(false); }} style={{
              width: "100%", display: "flex", alignItems: "center", gap: 11, padding: "10px 14px",
              background: current.type === "personal" ? T.g50 : "transparent", border: "none", cursor: "pointer", fontFamily: T.sans, textAlign: "left",
            }}>
              <div style={{ width: 30, height: 30, borderRadius: 9, background: "#eff6ff", border: "1px solid #bfdbfe", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11.5, fontWeight: 700, color: "#1d4ed8", flexShrink: 0 }}>
                {MOCK_PERSONAL_ACCOUNT.initials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.n900 }}>{MOCK_PERSONAL_ACCOUNT.name}</div>
                <div style={{ fontSize: 11, color: T.n400 }}>Personal account</div>
              </div>
              {current.type === "personal" && <Icon name="CheckCircle" size={15} color={T.g600} strokeWidth={2} />}
            </button>

            <div style={{ borderTop: `1px solid ${T.n100}`, padding: "8px" }}>
              <button style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "9px 10px", background: "transparent", border: "none", cursor: "pointer", fontFamily: T.sans, fontSize: 12.5, color: T.n500, borderRadius: 8 }}>
                <Icon name="Plus" size={14} strokeWidth={2} color={T.n400} />
                Add another account
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Notifications dropdown ───────────────────────────────────────────────────
function NotificationsDropdown({ notifications, onNavigate }) {
  const [open, setOpen] = useState(false);
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: 36, height: 36, borderRadius: 10, border: `1px solid ${T.n150}`, background: T.white,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", position: "relative",
      }}>
        <Icon name="Bell" size={16} color={T.n500} strokeWidth={1.8} />
        {unreadCount > 0 && (
          <span style={{ position: "absolute", top: 7, right: 8, width: 7, height: 7, borderRadius: "50%", background: T.g500, border: `1.5px solid ${T.white}` }} />
        )}
      </button>

      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0, width: 340,
            background: T.white, border: `1px solid ${T.n150}`, borderRadius: 14,
            boxShadow: "0 12px 36px rgba(0,0,0,.14)", zIndex: 50, overflow: "hidden",
          }}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.n900 }}>Notifications</span>
              {unreadCount > 0 && (
                <span style={{ fontSize: 11, fontWeight: 600, color: T.g600, background: T.g50, border: `1px solid ${T.g100}`, borderRadius: 99, padding: "2px 8px" }}>
                  {unreadCount} new
                </span>
              )}
            </div>
            <div style={{ maxHeight: 360, overflowY: "auto" }}>
              {notifications.length === 0 ? (
                <div style={{ padding: "32px 16px", textAlign: "center" }}>
                  <p style={{ fontSize: 12.5, color: T.n400, margin: 0 }}>No notifications yet.</p>
                </div>
              ) : (
                notifications.map((n, i) => (
                  <button key={n.id} onClick={() => { onNavigate(n.linkTo); setOpen(false); }} style={{
                    width: "100%", display: "flex", gap: 11, padding: "12px 16px",
                    border: "none", cursor: "pointer", fontFamily: T.sans, textAlign: "left",
                    borderBottom: i < notifications.length - 1 ? `1px solid ${T.n100}` : "none",
                    background: n.read ? "transparent" : T.g50,
                  }}>
                    <div style={{ width: 30, height: 30, borderRadius: 9, background: T.white, border: `1px solid ${T.n150}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Icon name={n.icon} size={14} color={n.iconColor} strokeWidth={1.8} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: T.n900 }}>{n.title}</div>
                      <div style={{ fontSize: 12, color: T.n500, marginTop: 2, lineHeight: 1.5 }}>{n.body}</div>
                      <div style={{ fontSize: 11, color: T.n300, marginTop: 4 }}>{n.time}</div>
                    </div>
                    {!n.read && <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.g500, flexShrink: 0, marginTop: 5 }} />}
                  </button>
                ))
              )}
            </div>
            <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.n100}` }}>
              <button onClick={() => { onNavigate("/notifications"); setOpen(false); }} style={{
                width: "100%", background: "none", border: "none", cursor: "pointer",
                color: T.g600, fontSize: 12, fontWeight: 600, fontFamily: T.sans, padding: "4px 0",
              }}>
                View all notifications
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Messages dropdown ────────────────────────────────────────────────────────
function MessagesDropdown({ messages, onNavigate }) {
  const [open, setOpen] = useState(false);
  const unreadCount = messages.filter(m => m.unread).length;

  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: 36, height: 36, borderRadius: 10, border: `1px solid ${T.n150}`, background: T.white,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", position: "relative",
      }}>
        <MessageIcon size={16} color={T.n500} strokeWidth={1.8} />
        {unreadCount > 0 && (
          <span style={{ position: "absolute", top: 7, right: 8, width: 7, height: 7, borderRadius: "50%", background: T.g500, border: `1.5px solid ${T.white}` }} />
        )}
      </button>

      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0, width: 340,
            background: T.white, border: `1px solid ${T.n150}`, borderRadius: 14,
            boxShadow: "0 12px 36px rgba(0,0,0,.14)", zIndex: 50, overflow: "hidden",
          }}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.n900 }}>Messages</span>
              {unreadCount > 0 && (
                <span style={{ fontSize: 11, fontWeight: 600, color: T.g600, background: T.g50, border: `1px solid ${T.g100}`, borderRadius: 99, padding: "2px 8px" }}>
                  {unreadCount} unread
                </span>
              )}
            </div>
            <div style={{ maxHeight: 360, overflowY: "auto" }}>
              {messages.length === 0 ? (
                <div style={{ padding: "32px 16px", textAlign: "center" }}>
                  <p style={{ fontSize: 12.5, color: T.n400, margin: 0 }}>No messages yet.</p>
                </div>
              ) : (
                messages.map((m, i) => (
                  <button key={m.id} onClick={() => { onNavigate(`/messages/${m.conversationId}`); setOpen(false); }} style={{
                    width: "100%", display: "flex", gap: 11, padding: "12px 16px", textAlign: "left",
                    border: "none", cursor: "pointer", fontFamily: T.sans,
                    borderBottom: i < messages.length - 1 ? `1px solid ${T.n100}` : "none",
                    background: m.unread ? T.g50 : "transparent",
                  }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: m.colorSet.bg, border: `1.5px solid ${m.colorSet.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11.5, fontWeight: 700, color: m.colorSet.text, flexShrink: 0 }}>
                      {m.initials}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n900 }}>{m.name}</span>
                        <span style={{ fontSize: 10.5, color: T.n300, flexShrink: 0 }}>{m.time}</span>
                      </div>
                      <div style={{ fontSize: 12, color: T.n500, marginTop: 2, lineHeight: 1.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.preview}</div>
                    </div>
                    {m.unread && <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.g500, flexShrink: 0, marginTop: 5 }} />}
                  </button>
                ))
              )}
            </div>
            <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.n100}` }}>
              <button onClick={() => { onNavigate?.("/messages"); setOpen(false); }} style={{
                width: "100%", background: "none", border: "none", cursor: "pointer",
                color: T.g600, fontSize: 12, fontWeight: 600, fontFamily: T.sans, padding: "4px 0",
              }}>
                View all messages
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Loading screen ───────────────────────────────────────────────────────────
function DashboardLoading() {
  return (
    <div style={{ fontFamily: "DM Sans, sans-serif", minHeight: "100vh", background: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 40, height: 40, border: "3px solid #e5e7eb", borderTopColor: "#16a34a", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
        <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>Loading your dashboard...</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── No business account screen ───────────────────────────────────────────────
function NoBusiness({ navigate }) {
  return (
    <div style={{ fontFamily: "DM Sans, sans-serif", minHeight: "100vh", background: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", maxWidth: 400, padding: "0 24px" }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🏢</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 8 }}>No business account found</h2>
        <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 24, lineHeight: 1.6 }}>
          You don't have a business account yet, or you haven't been added to one as a team member.
        </p>
        <button
          onClick={() => navigate("/signup/business")}
          style={{ padding: "12px 24px", borderRadius: 10, border: "none", background: "#16a34a", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "DM Sans, sans-serif" }}>
          Create a business account
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════════

export default function BusinessDashboard() {
  useFonts();
  const navigate = useNavigate();

  // ── Context from BusinessAccountProvider (set in App.js) ─────────────────
  const {
    account: ctxAccount,
    setAccount: setCtxAccount,
    plan: ctxPlan,
    userRole,
    loading: ctxLoading,
  } = useBusinessAccount();

  // ── Local UI state ────────────────────────────────────────────────────────
  const [activeTab,     setActiveTab]     = useState("overview");
  const [settingsOpen,  setSettingsOpen]  = useState(false);
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);
  const [messages,      setMessages]      = useState(MOCK_MESSAGES);
  const [bookingsUsed,  setBookingsUsed]  = useState(0);
  const [accountType,   setAccountType]   = useState("business");

  // ── Derive account + plan: context if available, mock as fallback ─────────
  const account = ctxAccount ?? MOCK_BUSINESS_ACCOUNT;
  const plan    = ctxPlan    ?? PLAN_LIMITS[MOCK_BUSINESS_ACCOUNT.plan] ?? PLAN_LIMITS.starter;
  const role    = userRole   ?? "Owner";
  const isOwner = role === "Owner";

  // ── Subscription past-due flag ────────────────────────────────────────────
  const isPastDue = account?.subscription_status === "past_due";

  // ── Role-based tab filtering ──────────────────────────────────────────────
  const visibleTabs = NAV_ITEMS.filter(item =>
    (ROLE_TABS[role] ?? ROLE_TABS.Viewer).includes(item.id)
  );

  // ── Booking count for this month ──────────────────────────────────────────
  useEffect(() => {
    if (!account?.id) return;

    const loadBookingCount = async () => {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      const { count } = await supabase
        .from("bookings")
        .select("id", { count: "exact", head: true })
        .eq("business_id", account.id)
        .eq("billing_type", "subscription")
        .gte("created_at", startOfMonth);

      setBookingsUsed(count ?? 0);
    };

    loadBookingCount();
  }, [account?.id]);

  // ── Notifications ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!account?.id) return;

    const loadNotifications = async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("id, icon, icon_color, title, body, read, link_to, created_at")
        .eq("recipient_id", account.id)
        .order("created_at", { ascending: false })
        .limit(20);

      if (!error && data && data.length > 0) {
        setNotifications(data.map(n => ({
          ...n,
          iconColor: n.icon_color,
          linkTo:    n.link_to,
          time:      new Date(n.created_at).toLocaleString(),
        })));
      }
    };

    loadNotifications();

    const channel = supabase
      .channel(`notifications:${account.id}`)
      .on("postgres_changes", {
        event: "INSERT", schema: "public", table: "notifications",
        filter: `recipient_id=eq.${account.id}`,
      }, (payload) => {
        const n = payload.new;
        setNotifications(prev => [{
          ...n,
          iconColor: n.icon_color,
          linkTo:    n.link_to,
          time:      new Date(n.created_at).toLocaleString(),
        }, ...prev]);
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [account?.id]);

  // ── Merge bookingsUsed into account object passed to sections ─────────────
  const accountWithUsage = { ...account, bookingsUsedThisMonth: bookingsUsed };

  // ── Account switcher ──────────────────────────────────────────────────────
  const currentAccountObj = accountType === "business"
    ? { ...account, type: "business" }
    : { ...MOCK_PERSONAL_ACCOUNT, type: "personal" };

  const handleSwitchAccount = (type) => {
    if (type === "personal") {
      navigate("/account");
      return;
    }
    setAccountType("business");
    setActiveTab("overview");
  };

  // ── Section renderer ──────────────────────────────────────────────────────
  const renderSection = () => {
    switch (activeTab) {
      case "overview":  return <OverviewSection  account={accountWithUsage} plan={plan} onNavigate={setActiveTab} />;
      case "team":      return <TeamSection      account={accountWithUsage} plan={plan} userRole={role} />;
      case "bookings":  return <BookingsSection  account={accountWithUsage} plan={plan} userRole={role} onUpdateAccount={setCtxAccount ?? (() => {})} />;
      case "locations": return <LocationsSection account={accountWithUsage} plan={plan} userRole={role} />;
      case "billing":   return <BillingSection   account={accountWithUsage} plan={plan} onUpdateAccount={setCtxAccount ?? (() => {})} onNavigate={setActiveTab} />;
      case "reports":   return <ReportsSection   account={accountWithUsage} userRole={role} />;
      case "members":   return <MembersSection   account={accountWithUsage} plan={plan} userRole={role} />;
      default:          return null;
    }
  };

  // ── Guards ────────────────────────────────────────────────────────────────
  if (ctxLoading) return <DashboardLoading />;
  if (!ctxAccount) return <NoBusiness navigate={navigate} />;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: T.sans, background: T.n50, minHeight: "100vh", color: T.n900, display: "flex" }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-thumb { background: ${T.n200}; border-radius: 99px; }
        ::-webkit-scrollbar-track { background: transparent; }
      `}</style>

      {/* ══ SIDEBAR ══════════════════════════════════════════════════════════ */}
      <div style={{
        width: 248, flexShrink: 0, background: T.white, borderRight: `1px solid ${T.n150}`,
        display: "flex", flexDirection: "column", height: "100vh", position: "sticky", top: 0,
      }}>
        {/* Logo */}
        <div style={{ padding: "20px 20px 16px", borderBottom: `1px solid ${T.n100}` }}>
          <AppLogo size="md" variant="dark" onClick={() => navigate("/")} />
          <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.n400, letterSpacing: "0.06em", textTransform: "uppercase" }}>Business</span>
            <PlanBadge plan={account.plan} />
          </div>
        </div>

        {/* Nav — role-filtered */}
        <div style={{ flex: 1, overflowY: "auto", padding: "14px 12px" }}>
          {visibleTabs.map(item => {
            const active = activeTab === item.id;
            return (
              <button key={item.id} onClick={() => setActiveTab(item.id)} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 11,
                padding: "10px 12px", borderRadius: 10, border: "none", cursor: "pointer",
                background: active ? T.g50 : "transparent", marginBottom: 2,
                fontFamily: T.sans, textAlign: "left",
              }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = T.n50; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}>
                <Icon name={item.icon} size={16} color={active ? T.g600 : T.n500} strokeWidth={1.8} />
                <span style={{ fontSize: 13.5, fontWeight: active ? 600 : 500, color: active ? T.g700 : T.n700, flex: 1 }}>{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Bottom: upgrade nudge (Owner + starter only) + settings */}
        <div style={{ padding: 12, borderTop: `1px solid ${T.n100}` }}>
          {plan.key === "starter" && isOwner && (
            <div style={{ padding: "14px", borderRadius: 12, background: `linear-gradient(135deg, ${T.g600}, ${T.g500})`, marginBottom: 10 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: T.white, marginBottom: 4 }}>Need more room to grow?</div>
              <p style={{ fontSize: 11.5, color: "rgba(255,255,255,.85)", margin: "0 0 10px", lineHeight: 1.5 }}>Upgrade to Business for more reserved Pros, bookings, and seats.</p>
              <button onClick={() => setActiveTab("billing")} style={{ width: "100%", padding: "8px", borderRadius: 8, border: "none", background: T.white, color: T.g700, fontSize: 12, fontWeight: 700, fontFamily: T.sans, cursor: "pointer" }}>
                Upgrade plan
              </button>
            </div>
          )}
          {isOwner && (
            <button onClick={() => setSettingsOpen(true)} style={{
              width: "100%", display: "flex", alignItems: "center", gap: 11, padding: "10px 12px",
              borderRadius: 10, border: "none", background: "transparent", cursor: "pointer", fontFamily: T.sans, textAlign: "left",
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.n50}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <Icon name="Settings" size={16} color={T.n500} strokeWidth={1.8} />
              <span style={{ fontSize: 13.5, fontWeight: 500, color: T.n700 }}>Settings</span>
            </button>
          )}
        </div>
      </div>

      {/* ══ MAIN ═════════════════════════════════════════════════════════════ */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>

        {/* Top bar */}
        <div style={{
          height: 64, flexShrink: 0, borderBottom: `1px solid ${T.n150}`, background: T.white,
          display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px",
        }}>
          <div>
            <h1 style={{ fontFamily: T.serif, fontSize: 19, fontWeight: 400, color: T.n900, margin: 0, letterSpacing: "-0.01em" }}>
              {visibleTabs.find(n => n.id === activeTab)?.label}
            </h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <MessagesDropdown messages={messages} onNavigate={navigate} />
            <NotificationsDropdown notifications={notifications} onNavigate={navigate} />
            <AccountSwitcher
              current={currentAccountObj}
              onSwitch={handleSwitchAccount}
              hasPersonalAccount={account.hasPersonalAccount ?? true}
              hasBusinessAccount={true}
            />
          </div>
        </div>

        {/* Past-due banner */}
        {isPastDue && (
          <div style={{
            padding: "12px 24px", background: "#fef2f2",
            borderBottom: "1px solid #fecaca",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Icon name="AlertTriangle" size={16} color="#dc2626" strokeWidth={2} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "#dc2626" }}>
                Your subscription payment is overdue. Some features may be restricted.
              </span>
            </div>
            <button
              onClick={() => setActiveTab("billing")}
              style={{
                padding: "8px 16px", borderRadius: 8, border: "none",
                background: "#dc2626", color: "#fff", fontSize: 12.5, fontWeight: 600,
                fontFamily: T.sans, cursor: "pointer", flexShrink: 0,
              }}>
              Pay now
            </button>
          </div>
        )}

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            {renderSection()}
          </div>
        </div>
      </div>

      <SettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        account={account}
        onUpdateAccount={setCtxAccount ?? (() => {})}
      />
    </div>
  );
}